import {capture} from "~/applicationAnalytics/core/capture";

// ── Route change callback system ─────────────────────────────────────────────
// Modules like scroll and time register here to reset on SPA navigation.

type RouteChangeCallback = (oldPath: string, newPath: string) => void;
const _routeChangeCallbacks: RouteChangeCallback[] = [];

/**
 * Register a callback that fires BEFORE the new pageview is captured.
 * Used by scroll.ts and time.ts to flush/reset on SPA navigation.
 */
export function onRouteChange(cb: RouteChangeCallback): void {
    _routeChangeCallbacks.push(cb);
}

// ── Pageview tracking ────────────────────────────────────────────────────────

let _lastPath = "";

export function capturePageView(beforeCapture?: () => void): void {
    const newPath = window.location.pathname;

    // Prevent duplicate pageviews for shallow routing (same path, e.g. query param changes)
    if (_lastPath === newPath) {
        beforeCapture?.();
        return;
    }

    // Fire route-change callbacks (skip on initial load when _lastPath is empty)
    if (_lastPath !== "") {
        for (const cb of _routeChangeCallbacks) {
            try {
                cb(_lastPath, newPath);
            } catch {
                /* ignore */
            }
        }
    }

    // Flush departure events with the old page context, then apply the destination.
    beforeCapture?.();
    _lastPath = newPath;

    capture("$pageview", {
        url: window.location.href,
        path: window.location.pathname,
        search: window.location.search || undefined,
        referrer: document.referrer || undefined,
        title: document.title,
    });
}

export function trackPageViews(mode: "history" | "router" = "history"): void {
    // Neptune records resolved routes explicitly; other SDK callers retain history tracking.
    if (mode === "router") {
        return;
    }
    // Fire immediately (or wait for DOM ready)
    if (document.readyState === "complete" || document.readyState === "interactive") {
        capturePageView();
    } else {
        window.addEventListener("DOMContentLoaded", () => capturePageView(), {once: true});
    }

    // ── SPA support: patch history API ────────────────────────────
    // React Router, Next.js Pages Router, Vue Router all call pushState
    const _originalPushState = history.pushState.bind(history);
    const _originalReplaceState = history.replaceState.bind(history);

    history.pushState = (...args: Parameters<typeof history.pushState>) => {
        _originalPushState(...args);
        capturePageView();
    };

    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
        _originalReplaceState(...args);
        capturePageView();
    };

    // Browser back / forward
    window.addEventListener("popstate", () => capturePageView());
}
